import { Workspace, SidebarItem } from "./account";

// Re-export SidebarItem from account.ts for backwards compatibility
export type { SidebarItem } from "./account";

/**
 * Hierarchical filters that can be applied at dashboard, tab, or widget level.
 */
export interface HierarchicalFilters {
  trackers?: string[] | null;
  date_range?: string | null; // "last_7_days", "last_30_days", "last_90_days", "custom"
  date_from?: string | null; // ISO format date string
  date_to?: string | null; // ISO format date string
  search?: string | null;
  filters?: Record<string, unknown> | null; // Additional ES filters
  independent?: boolean; // If true, ignore parent filters
}

/**
 * Request to update sidebar configuration via workspace.
 * Used with PATCH /workspaces/{id}
 */
export interface UpdateSidebarRequest {
  sidebar_items?: SidebarItem[];
  sidebar_width?: number;
  sidebar_show_favorites?: boolean;
  sidebar_compact_mode?: boolean;
  sidebar_locked?: boolean;
  sidebar_expanded_items?: string[];
}

// ============================================================================
// Helper functions (updated to work with Workspace)
// ============================================================================

/**
 * Get an item from the workspace sidebar by its ID.
 */
export function getSidebarItem(
  workspace: Workspace | null,
  itemId: string
): SidebarItem | null {
  return workspace?.sidebar_items?.find((item) => item.item_id === itemId) || null;
}

/**
 * Get all pinned items from the workspace sidebar.
 */
export function getPinnedItems(workspace: Workspace | null): SidebarItem[] {
  return (workspace?.sidebar_items?.filter((item) => item.pinned) || []).sort(
    (a, b) => a.order - b.order
  );
}

/**
 * Get all items of a specific type from the workspace sidebar.
 */
export function getItemsByType(
  workspace: Workspace | null,
  itemType: "dashboard" | "group"
): SidebarItem[] {
  return (
    workspace?.sidebar_items?.filter((item) => item.item_type === itemType) || []
  ).sort((a, b) => a.order - b.order);
}

/**
 * Check if an item is expanded in the workspace sidebar.
 */
export function isItemExpanded(
  workspace: Workspace | null,
  itemId: string
): boolean {
  return workspace?.sidebar_expanded_items?.includes(itemId) || false;
}
